(() => {
  'use strict';

  const STORAGE_KEY = 'wellServedCartFireworks';
  const QTY_SNAPSHOT_KEY = 'wellServedCartQty';
  const MAX_FLAG_AGE = 300000;
  const QTY_SNAPSHOT_MAX_AGE = 2592000000;
  const EFFECT_DURATION = 2400;

  const normalizePath = (value) => {
    try {
      return new URL(value, window.location.origin)
        .pathname
        .replace(/\.js$/, '')
        .replace(/\/$/, '');
    } catch (error) {
      return String(value || '')
        .replace(/\.js$/, '')
        .replace(/\/$/, '');
    }
  };

  const cartAddPath = () =>
    normalizePath(
      window.routes?.cart_add_url ||
      `${window.Shopify?.routes?.root || '/'}cart/add`
    );

  const isCartAddUrl = (value) =>
    normalizePath(value) === cartAddPath();

  const markCelebration = () => {
    const expires = new Date(
      Date.now() + MAX_FLAG_AGE
    ).toUTCString();

    document.cookie =
      `${STORAGE_KEY}=${Date.now()}` +
      `; expires=${expires}` +
      '; path=/' +
      '; SameSite=Lax';
  };

  const consumeCelebration = () => {
    const cookiePrefix = `${STORAGE_KEY}=`;

    const cookie = document.cookie
      .split('; ')
      .find((item) =>
        item.startsWith(cookiePrefix)
      );

    document.cookie =
      `${STORAGE_KEY}=` +
      '; expires=Thu, 01 Jan 1970 00:00:00 GMT' +
      '; path=/' +
      '; SameSite=Lax';

    if (!cookie) return false;

    const timestamp = Number(
      cookie.substring(cookiePrefix.length)
    );

    return (
      Number.isFinite(timestamp) &&
      Date.now() - timestamp <= MAX_FLAG_AGE
    );
  };

  const getStoredCartQty = () => {
    const cookiePrefix = `${QTY_SNAPSHOT_KEY}=`;

    const cookie = document.cookie
      .split('; ')
      .find((item) =>
        item.startsWith(cookiePrefix)
      );

    if (!cookie) return null;

    const value = Number(
      cookie.substring(cookiePrefix.length)
    );

    return Number.isFinite(value) ? value : null;
  };

  const storeCartQty = (qty) => {
    const expires = new Date(
      Date.now() + QTY_SNAPSHOT_MAX_AGE
    ).toUTCString();

    document.cookie =
      `${QTY_SNAPSHOT_KEY}=${qty}` +
      `; expires=${expires}` +
      '; path=/' +
      '; SameSite=Lax';
  };

  // Catch-all safety net: some third-party apps (e.g. bundle/upsell widgets
  // like Wide Bundle) add to cart in ways that are invisible to JS-level
  // instrumentation entirely -- for example navigating the browser straight
  // to a Shopify cart permalink (`/cart/add?id=...&quantity=...`) rather
  // than submitting a form or making a fetch/XHR call. There is no reliable
  // way to intercept that ahead of time, so instead we compare the cart's
  // item count on every page load against the last value we saw. If it
  // went up since the last page view, something was added -- regardless of
  // how -- so we mark the celebration. This runs in addition to (not
  // instead of) the more immediate detections below, which still make the
  // fireworks feel instant for the flows they can see.
  const checkCartQtyForIncrease = async () => {
    try {
      const response = await window.fetch('/cart.js', {
        credentials: 'same-origin'
      });

      if (!response.ok) return;

      const cart = await response.json();
      const currentQty = Number(cart.item_count);

      if (!Number.isFinite(currentQty)) return;

      const storedQty = getStoredCartQty();

      if (
        storedQty !== null &&
        currentQty > storedQty
      ) {
        markCelebration();
      }

      storeCartQty(currentQty);
    } catch (error) {
      // Never let detection logic block the page.
    }
  };

  const connectFlowCartEvent = () => {
    if (
      window.eventBus &&
      typeof window.eventBus.on === 'function'
    ) {
      window.eventBus.on(
        'cart:added',
        markCelebration
      );

      return;
    }

    window.setTimeout(
      connectFlowCartEvent,
      100
    );
  };

  connectFlowCartEvent();

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      const action =
        form.getAttribute('action') ||
        form.action ||
        '';

      if (isCartAddUrl(action)) {
        markCelebration();
      }
    },
    true
  );

  // Some third-party apps (e.g. bundle/upsell widgets like Wide Bundle) add
  // items to the cart by building a hidden <form action="/cart/add"> and
  // calling form.submit() directly instead of clicking a real submit
  // button. Per the DOM spec, HTMLFormElement.submit() does NOT dispatch a
  // "submit" event, so the listener above never sees it, and since it's a
  // real full-page form post (not a fetch/XHR call) the fetch override
  // below never sees it either. Patching the submit() method itself is the
  // only way to reliably catch this path.
  const patchFormSubmit = () => {
    const proto =
      window.HTMLFormElement &&
      window.HTMLFormElement.prototype;

    if (!proto || proto.__wellServedFireworksPatched) {
      return;
    }

    const originalSubmit = proto.submit;

    proto.submit = function patchedSubmit(...args) {
      try {
        const action =
          this.getAttribute('action') ||
          this.action ||
          '';

        if (isCartAddUrl(action)) {
          markCelebration();
        }
      } catch (error) {
        // Never let detection logic block the real submit.
      }

      return originalSubmit.apply(this, args);
    };

    proto.__wellServedFireworksPatched = true;
  };

  patchFormSubmit();

  const originalFetch =
    window.fetch.bind(window);

  window.fetch = async (...args) => {
    const request = args[0];

    const requestUrl =
      typeof request === 'string' ||
      request instanceof URL
        ? String(request)
        : request?.url || '';

    const response =
      await originalFetch(...args);

    if (
      isCartAddUrl(requestUrl) &&
      response.ok
    ) {
      markCelebration();
    }

    return response;
  };

  const isCartPageWithItems = () => {
    const currentPath =
      normalizePath(
        window.location.pathname
      );

    const expectedCartPath =
      normalizePath(
        window.routes?.cart_url ||
        `${window.Shopify?.routes?.root || '/'}cart`
      );

    const isCartPage =
      document.body.classList.contains(
        'template-cart'
      ) ||
      currentPath === expectedCartPath;

    if (!isCartPage) return false;

    return Boolean(
      document.querySelector(
        [
          'form[data-wetheme-section-type="template--cart"]',
          '.cart-table tbody .cart__row',
          '.cart-table tbody tr'
        ].join(', ')
      )
    );
  };

  const launchFireworks = () => {
    document
      .getElementById(
        'well-served-fireworks'
      )
      ?.remove();

    const canvas =
      document.createElement('canvas');

    const context =
      canvas.getContext('2d');

    if (!context) return;

    canvas.id =
      'well-served-fireworks';

    canvas.setAttribute(
      'aria-hidden',
      'true'
    );

    Object.assign(canvas.style, {
      position: 'fixed',
      inset: '0',
      width: '100vw',
      height: '100vh',
      pointerEvents: 'none',
      zIndex: '2147483647'
    });

    document.body.appendChild(canvas);

    let width = 0;
    let height = 0;
    let ratio = 1;
    let frameId = 0;

    const particles = [];

    const colors = [
      '#D7263D',
      '#FFFFFF',
      '#1B4D89',
      '#F4C95D',
      '#FF6B35'
    ];

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;

      ratio = Math.min(
        window.devicePixelRatio || 1,
        2
      );

      canvas.width = Math.round(
        width * ratio
      );

      canvas.height = Math.round(
        height * ratio
      );

      canvas.style.width =
        `${width}px`;

      canvas.style.height =
        `${height}px`;

      context.setTransform(
        ratio,
        0,
        0,
        ratio,
        0,
        0
      );
    };

    const burst = (
      x,
      y,
      count = 70
    ) => {
      for (
        let index = 0;
        index < count;
        index += 1
      ) {
        const angle =
          Math.random() *
          Math.PI *
          2;

        const speed =
          2.5 +
          Math.random() *
          6.5;

        particles.push({
          x,
          y,
          oldX: x,
          oldY: y,
          vx:
            Math.cos(angle) *
            speed,
          vy:
            Math.sin(angle) *
            speed,
          gravity:
            0.055 +
            Math.random() *
            0.03,
          friction: 0.982,
          alpha: 1,
          decay:
            0.011 +
            Math.random() *
            0.012,
          size:
            1.5 +
            Math.random() *
            2.8,
          color:
            colors[
              Math.floor(
                Math.random() *
                colors.length
              )
            ]
        });
      }
    };

    const startedAt =
      performance.now();

    const animate = (now) => {
      context.clearRect(
        0,
        0,
        width,
        height
      );

      context.globalCompositeOperation =
        'lighter';

      for (
        let index =
          particles.length - 1;
        index >= 0;
        index -= 1
      ) {
        const particle =
          particles[index];

        particle.oldX =
          particle.x;

        particle.oldY =
          particle.y;

        particle.vx *=
          particle.friction;

        particle.vy *=
          particle.friction;

        particle.vy +=
          particle.gravity;

        particle.x +=
          particle.vx;

        particle.y +=
          particle.vy;

        particle.alpha -=
          particle.decay;

        context.beginPath();

        context.moveTo(
          particle.oldX,
          particle.oldY
        );

        context.lineTo(
          particle.x,
          particle.y
        );

        context.lineWidth =
          particle.size;

        context.lineCap =
          'round';

        context.strokeStyle =
          particle.color;

        context.globalAlpha =
          Math.max(
            particle.alpha,
            0
          );

        context.stroke();

        if (
          particle.alpha <= 0
        ) {
          particles.splice(
            index,
            1
          );
        }
      }

      context.globalAlpha = 1;

      context.globalCompositeOperation =
        'source-over';

      if (
        now - startedAt <
          EFFECT_DURATION ||
        particles.length
      ) {
        frameId =
          requestAnimationFrame(
            animate
          );
      } else {
        window.removeEventListener(
          'resize',
          resize
        );

        canvas.remove();
      }
    };

    resize();

    window.addEventListener(
      'resize',
      resize,
      { passive: true }
    );

    const burstLocations = [
      [0.16, 0.3, 0],
      [0.5, 0.2, 180],
      [0.84, 0.32, 360],
      [0.32, 0.48, 560],
      [0.68, 0.46, 760]
    ];

    burstLocations.forEach(
      ([x, y, delay]) => {
        window.setTimeout(
          () =>
            burst(
              width * x,
              height * y
            ),
          delay
        );
      }
    );

    frameId =
      requestAnimationFrame(
        animate
      );

    window.setTimeout(() => {
      if (canvas.isConnected) {
        cancelAnimationFrame(
          frameId
        );

        window.removeEventListener(
          'resize',
          resize
        );

        canvas.remove();
      }
    }, 6000);
  };

  const initialize = async () => {
    const query =
      new URLSearchParams(
        window.location.search
      );

    const testMode =
      query.get('fireworks') ===
      'test';

    const onCartPage = isCartPageWithItems();

    // Always keep the quantity snapshot fresh, even off the cart page, so
    // it never goes stale and causes a false-positive on a later visit.
    // On the cart page itself, this doubles as an immediate check for the
    // permalink/navigation-based add-to-cart flows described above.
    await checkCartQtyForIncrease();

    const celebrationResult = testMode ? true : consumeCelebration();

    if (!onCartPage) {
      return;
    }

    if (!celebrationResult) {
      return;
    }

    window.setTimeout(
      launchFireworks,
      250
    );
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }
})();
